/**
 * #605 — Papan's own picker for an app's `papan.files.pickAndRead`: what the
 * user sees when an app asks for files. Rendered by the picker PAGE
 * (`/__handoff/<pid>`), which is shown as a modal iframe in Papan and as a
 * popup on pd.app — never inside the app's document, so an app can neither
 * draw a fake one nor read the listing.
 *
 * It answers ONE request (`handoffApi`): the app's options come from the
 * server's record of the request, the choice is measured (a dry run of the
 * exact resolver the answer uses) before it can be confirmed, and confirming
 * mints a grant for the APP's session. Nothing here talks to the app.
 *
 * UX rules (docs/plans/2026-09-18_PDAPP_FILE_HANDOFF.md §9.1): the title says
 * who asks and what for; files the app can't use are dimmed, not hidden, with
 * the reason on hover; the button names the action; the footer says what will
 * be shared, or why it can't be, before anything is sent.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import {
  HANDOFF_LIMITS,
  formatStorageBytes,
  isLiveMountRow,
  matchesHandoffFilter,
  stripPointerSuffix,
  type HandoffCapRefusal,
  type HandoffReadPolicy,
  type HandoffRequestView,
} from '@papan/types'
import type { FileListItem } from '@papan/files/client'
import { handoffApi } from '@papan/files/client/handoff'
import { useIsDesktop } from '@papan/core-ui/ui'
import { useArmed } from './useArmed'
import { HandoffHeader } from './HandoffHeader'
import { useTypingCarry } from './useTypingCarry'
import { PickerNavigator, getRootFolderId, readableName, type PickerCrumb, type PickerSelection, type RowChoice, type RowGesture } from '../PickerNavigator'

/** Permissions that may hand bytes out — the drive's download floor. */
const COPY_PERMS = new Set(['readcopy', 'readwrite', 'admin', 'owner'])
const canCopy = (perm: string | undefined) => perm === undefined || COPY_PERMS.has(perm)

type Measure =
  | { state: 'idle' }
  | { state: 'measuring' }
  /** `count`: how many were chosen when measured — a choice changed since is
   *  still being measured, never read against the new count. */
  | { state: 'ok'; files: number; bytes: number; count: number }
  | { state: 'refused'; refusal: HandoffCapRefusal }
  | { state: 'unavailable' }
  /** The check itself failed (network, a server hiccup) — asked again shortly. */
  | { state: 'retrying' }
  /** The check failed in a way asking again by itself would not mend — the
   *  person may try again. Never a verdict on the choice. */
  | { state: 'checkFailed' }

/** How long before a check that failed on the way is first asked again —
 *  doubling after each failure in a row, up to `HANDOFF_RETRY_MAX_MS`. */
export const HANDOFF_RETRY_MS = 3_000
const HANDOFF_RETRY_MAX_MS = 30_000

/**
 * Whether a failed call is worth asking again by itself, and when: the
 * network, a timeout (408), a rate limit (its own wait) or a server hiccup —
 * for as long as the page is open, backing off. Anything else — a 4xx that
 * will answer the same next time — is not: null (the person may try again).
 */
export function retryDelay(err: { status?: number; retryAfterSec?: number } | undefined, failures = 1, random = Math.random): number | null {
  const status = err?.status
  if (status && status !== 408 && status !== 429 && status < 500) return null
  const sec = err?.retryAfterSec
  // Never sooner than the server asked; spread out, so pickers that failed
  // together in an outage do not all come back at the same moment.
  if (typeof sec === 'number' && sec > 0) return Math.min(sec * 1000, HANDOFF_RETRY_MAX_MS) * (1 + 0.25 * random())
  return Math.min(HANDOFF_RETRY_MS * 2 ** Math.max(0, failures - 1), HANDOFF_RETRY_MAX_MS) * (0.75 + 0.5 * random())
}

/** A read request, as the picker page describes it. */
export type ReadRequestView = Extract<HandoffRequestView, { kind: 'read' }>

export interface HandoffPickerProps {
  pid: string
  view: ReadRequestView
  /** The tenant this page browses (its URL decides it). */
  tenantId: string
  /** Beside the title — the page's organisation switcher. */
  headerAside?: ReactNode
  /** Where to open (the last folder used here). */
  initialFolderId?: string | null
  onFolderChange?: (folderId: string) => void
  /** The request is settled — answered, or given up. */
  onFinished: (status: 'answered' | 'cancelled') => void
}

export function HandoffPicker({ pid, view, tenantId, headerAside, initialFolderId, onFolderChange, onFinished }: HandoffPickerProps) {
  const { t } = useTranslation('files')
  const { policy } = view
  const app = view.appName || t('handoff.anApp')
  const rootId = getRootFolderId(tenantId)
  const roots = useMemo(() => [{ id: rootId, label: 'papan' }], [rootId])
  const single = policy.selection.max === 1

  const [checked, setChecked] = useState<Map<string, PickerSelection>>(() => new Map())
  const [openFolder, setOpenFolder] = useState<{ id: string; name: string; permission?: string } | null>(null)
  const [measure, setMeasure] = useState<Measure>({ state: 'idle' })
  const [submitting, setSubmitting] = useState(false)
  const [closed, setClosed] = useState(false)
  /** Confirming failed on the way (or was rate-limited: `busy`), and the
   *  request is still open — say so; Open stays. */
  const [sendFailed, setSendFailed] = useState<false | 'failed' | 'busy'>(false)
  const [measureRetry, setMeasureRetry] = useState(0)
  /** The person's "Try again": a fresh check, not a retry in a row. */
  const [measureAgain, setMeasureAgain] = useState(0)
  // Which retry the last measure was, and how many in a row have failed —
  // a new choice starts afresh (and says "checking", not "trying again").
  const measureRetrySeen = useRef(0)
  const measureFailures = useRef(0)

  const checkedIds = useMemo<ReadonlySet<string>>(() => new Set(checked.keys()), [checked])
  const foldersChoosable = policy.select !== 'file'
  const filesChoosable = policy.select !== 'folder'

  const rowChoice = useCallback((item: FileListItem, parentPermission?: string): RowChoice => {
    const perm = item.effectivePermission ?? item.permission ?? parentPermission
    if (item.isFolder) {
      if (!foldersChoosable) return false
      return canCopy(perm) ? true : t('handoff.reason.folderNotShareable')
    }
    if (!filesChoosable) return t('handoff.reason.foldersOnly')
    // A blob-less row holding only a link's metadata mirror (a Conflict copy, or an old
    // link renamed off its suffix whose bytes were never moved into a blob) is not
    // content — the server leaves it out of any handoff (resolve.ts `handsOver`). A link
    // renamed off its suffix is otherwise a plain file (I125) and hands over its bytes.
    const link = isLiveMountRow(item.name, item.fileType)
    if (!link && !item.contentHash && item.metadataJson) return t('handoff.reason.noContent')
    // A link is judged by its name alone, suffix stripped (`Report.pdf.chain`
    // is a .pdf): the row's own type is the LINK's (a `.shared` is stored as
    // JSON), not the target's — the server judges it the same way.
    const byName = link ? { name: stripPointerSuffix(item.name), mime: null } : { name: item.name, mime: item.mimeType }
    if (!matchesHandoffFilter(byName, policy.filter)) return t('handoff.reason.filtered', { app })
    return canCopy(perm) ? true : t('handoff.reason.viewOnly')
  }, [foldersChoosable, filesChoosable, policy.filter, app, t])

  // The choice as it stood before the LAST click on a row — undone when that
  // click turns out to be the first of a double-click that opens a folder
  // (`onOpenByDoubleClick`): the person was going in, not choosing. A choice
  // they made on purpose, earlier, stands.
  const lastToggleRef = useRef<{ id: string; before: Map<string, PickerSelection> } | null>(null)

  // How a click chooses. When several may be chosen: ticks, on every device —
  // each click or tap adds or takes out one, Shift-click adds a range. When
  // one is the answer, the row is the choice: a click chooses it.
  const ticks = !single
  const finePointer = useFinePointer()
  // A phone (a touch device below the desktop width): one bar at the bottom —
  // a line that says only what the buttons don't, and the two buttons across
  // the width. Anywhere with a mouse or trackpad keeps the desktop's rows (a
  // narrow window is no phone).
  const phoneBar = usePhoneBar()

  const toggle = useCallback((item: PickerSelection, gesture?: RowGesture) => {
    setChecked(prev => {
      lastToggleRef.current = { id: item.id, before: prev }
      if (single) return new Map([[item.id, item]]) // a radio: choosing again keeps it
      // Past the app's maximum a choice still lands, and the footer says why
      // it can't be confirmed — a choice silently ignored explains nothing.
      switch (gesture?.how) {
        case 'only':
          return new Map([[item.id, item]])
        case 'range':
          return new Map((gesture.range ?? [item]).map(r => [r.id, r]))
        case 'range-add': {
          const next = new Map(prev)
          for (const r of gesture.range ?? [item]) next.set(r.id, r)
          return next
        }
        default: {
          // A tick, or Ctrl/Cmd: add or take out.
          const next = new Map(prev)
          if (next.has(item.id)) next.delete(item.id)
          else next.set(item.id, item)
          return next
        }
      }
    })
  }, [single])

  const onNavigate = useCallback((folderId: string | null, crumbs: PickerCrumb[], permission?: string) => {
    setOpenFolder(folderId ? { id: folderId, name: crumbs[crumbs.length - 1]?.name ?? '', permission } : null)
    if (folderId) onFolderChange?.(folderId)
  }, [onFolderChange])

  // Select all / Deselect all over the list on screen: adds every row that may
  // be chosen (keeping what was chosen elsewhere), or takes them all out.
  const toggleAll = useCallback((items: PickerSelection[], select: boolean) => {
    lastToggleRef.current = null
    setChecked(prev => {
      const next = new Map(prev)
      for (const item of items) {
        if (select) next.set(item.id, item)
        else next.delete(item.id)
      }
      return next
    })
  }, [])

  const onOpenByDoubleClick = useCallback((item: PickerSelection) => {
    const last = lastToggleRef.current
    lastToggleRef.current = null
    if (last?.id === item.id) setChecked(last.before)
  }, [])

  // What confirming would send. In a folders-only pick, the folder you are IN
  // is the choice until you tick something (the drive pickers' convention) —
  // never the drive root, where the picker opens: the whole drive must not be
  // the answer to one click the person did not aim.
  const usingOpenFolder = checked.size === 0 && policy.select === 'folder' && !!openFolder
    && openFolder.id !== rootId && canCopy(openFolder.permission)
  const choiceIds = useMemo(
    () => (checked.size > 0 ? [...checked.keys()] : usingOpenFolder ? [openFolder!.id] : []),
    [checked, usingOpenFolder, openFolder],
  )

  // Measure every choice before it can be confirmed — the same resolver the
  // answer runs, so the line in the footer is what would be sent.
  const measureSeq = useRef(0)
  const overMax = choiceIds.length > policy.selection.max
  useEffect(() => {
    const seq = ++measureSeq.current
    // Nothing to measure — or a count the server refuses anyway ("at most N").
    setSendFailed(false)
    if (choiceIds.length === 0 || overMax) { setMeasure({ state: 'idle' }); return }
    const isRetry = measureRetry !== measureRetrySeen.current
    measureRetrySeen.current = measureRetry
    if (!isRetry) measureFailures.current = 0
    // A retry keeps saying it is retrying, rather than flicker back to "checking".
    setMeasure(m => (isRetry && m.state === 'retrying' ? m : { state: 'measuring' }))
    let retry: ReturnType<typeof setTimeout> | undefined
    const timer = setTimeout(() => {
      handoffApi.measure(pid, choiceIds).then(
        (m) => {
          if (seq !== measureSeq.current) return
          if (m.ok) setMeasure({ state: 'ok', files: m.files, bytes: m.bytes, count: choiceIds.length })
          else if (m.reason === 'unavailable') setMeasure({ state: 'unavailable' })
          else setMeasure({ state: 'refused', refusal: m })
        },
        (err: { status?: number; retryAfterSec?: number }) => {
          if (seq !== measureSeq.current) return
          if (err?.status === 409) { setClosed(true); return }
          // The check failed on the way — not a verdict on the choice. Ask
          // again, backing off; a failure that would answer the same is left
          // to the person to try again.
          const wait = retryDelay(err, ++measureFailures.current)
          if (wait === null) { setMeasure({ state: 'checkFailed' }); return }
          setMeasure({ state: 'retrying' })
          retry = setTimeout(() => setMeasureRetry(n => n + 1), wait)
        },
      )
    }, 250)
    return () => { clearTimeout(timer); clearTimeout(retry) }
  }, [choiceIds, overMax, pid, measureRetry, measureAgain])

  const inRange = choiceIds.length >= policy.selection.min && choiceIds.length <= policy.selection.max
  const armed = useArmed()
  const carry = useTypingCarry()
  const canConfirm = armed && inRange && measure.state === 'ok' && !submitting && !closed

  const confirm = useCallback(async (ids: string[]) => {
    // Keys carried over from the app (a burst going when the picker appeared —
    // Tab onto a tick, Space, Enter) are not the person answering. A click is:
    // the pointer press ends the carry first.
    if (carry.carrying()) return
    setSubmitting(true)
    setSendFailed(false)
    try {
      const res = await handoffApi.answerRead(pid, ids)
      if (res.ok) { onFinished('answered'); return }
      if (res.reason === 'unavailable') setMeasure({ state: 'unavailable' })
      else setMeasure({ state: 'refused', refusal: res })
    } catch (err) {
      // Lost on the way — or refused as already settled, after an earlier
      // try whose reply was lost: the answer may have landed. If it did, it
      // stands — the person is not told it failed while the app gets the files.
      if (await answeredAfterAll(pid)) onFinished('answered')
      else if ((err as { status?: number })?.status === 409) setClosed(true)
      else setSendFailed((err as { status?: number })?.status === 429 ? 'busy' : 'failed')
    } finally {
      setSubmitting(false)
    }
  }, [pid, onFinished, carry])

  const cancel = useCallback(() => {
    // Hand back the refusal the user gave up on, so the app can say why — and
    // say "cancelled" only once the server has it: the host reads the outcome
    // the moment it hears, and would otherwise still find the request pending.
    // A cancel that never landed keeps the picker, and says so: "cancelled"
    // with the request still open would leave the app waiting it out.
    void handoffApi.cancel(pid, measure.state === 'refused' ? measure.refusal : undefined)
      .then((landed) => { if (landed) onFinished('cancelled'); else setSendFailed('failed') })
  }, [pid, measure, onFinished])

  // Escape closes (the search field keeps Escape for itself first). Enter
  // confirms a choice the person MADE here — unless focus is on a control
  // whose own Enter means something (a row chooses or opens, the search field
  // searches, a button presses). Never a held key, and never the folder the
  // picker merely opened in: an Enter meant for the app that asked must not
  // answer for the person.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (!e.defaultPrevented) cancel(); return }
      if (e.key !== 'Enter' || e.repeat || !canConfirm || checked.size === 0) return
      if (!e.defaultPrevented && !isControl(e.target)) void confirm(choiceIds)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cancel, confirm, canConfirm, checked, choiceIds])

  const title = t(`handoff.title.${titleKey(policy.select, single)}`, { app })
  // What may be chosen — how many, and (where there is a mouse or trackpad to
  // Shift-click with) how to take a range — and what the app narrowed it to:
  // the footer's line says it before anything is chosen (the dimmed rows show
  // which can't be chosen, not why — and a finger has no hover). One footer,
  // desktop and phone alike; only its shape differs.
  const filterText = describeFilter(policy, t)
  const howMany = describeHowMany(policy, t)
  const howTo = !single && finePointer ? t('handoff.withRange', { howMany }) : howMany
  const rule = filterText ? `${howTo} · ${filterText}` : howTo
  // "1 file selected" when the choice is files alone; a folder's count is of
  // what is inside it, not of what was selected.
  const onlyFiles = !usingOpenFolder && [...checked.values()].every(i => !i.isFolder)
  const summary = (
    <div className={`min-w-0 text-xs ${phoneBar ? '' : 'truncate'}`} aria-live="polite" data-testid="handoff-summary">
      {closed
        ? <span className="themed-text-muted">{t('handoff.gone')}</span>
        : sendFailed
          ? <span className={sendFailed === 'busy' ? 'text-amber-500' : 'text-red-500'}>{t(sendFailed === 'busy' ? 'handoff.busy' : 'handoff.sendFailed')}</span>
          : <Summary measure={measure} app={app} policy={policy} count={choiceIds.length} onlyFiles={onlyFiles} onTryAgain={() => setMeasureAgain(n => n + 1)} rule={rule} />}
    </div>
  )
  const confirmButton = (shape: string) => (
    <button
      type="button"
      data-testid="handoff-confirm"
      onClick={() => void confirm(choiceIds)}
      disabled={!canConfirm}
      className={`flex items-center gap-1.5 ${shape} text-sm themed-btn-primary rounded transition-colors disabled:opacity-50`}
    >
      {submitting && <Loader2 size={13} className="animate-spin" />}
      {confirmLabel({ checked, usingOpenFolder, openFolderName: openFolder?.name ?? '', t })}
    </button>
  )

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="handoff-picker">
      <HandoffHeader title={title} aside={headerAside} />

      <PickerNavigator
        roots={roots}
        initialFolderId={initialFolderId ?? rootId}
        mode="fileOrFolder"
        selectedId={null}
        onSelect={() => {}}
        checkedIds={checkedIds}
        onToggleCheck={toggle}
        // Ticks when several may be chosen (a folder row opens, its tick
        // chooses it). Otherwise no ticks: a click on a file or folder chooses
        // it; a folder opens on double-click or its open button.
        checkStyle={ticks ? 'box' : 'row'}
        onToggleAll={ticks ? toggleAll : undefined}
        maxChecked={policy.selection.max}
        onOpenByDoubleClick={onOpenByDoubleClick}
        rowChoice={rowChoice}
        // Double-clicking a FILE chooses-and-confirms — only when one item is the
        // whole answer (a folder's double-click opens it).
        onConfirm={single ? (item) => { if (!armed) return; setChecked(new Map([[item.id, item]])); void confirm([item.id]) } : undefined}
        onNavigate={onNavigate}
        // A folder whose listing failed is not the open folder to offer.
        onNavigateFailed={() => setOpenFolder(null)}
        className="flex-1"
      />

      {/* One footer on both: only its shape changes, so the live line and the
          buttons keep their identity (and focus) when the width crosses. A
          phone's bar clears the home indicator. */}
      <footer
        className={`border-t themed-border ${phoneBar ? PHONE_BAR : 'flex items-center gap-3 px-4 py-3'}`}
      >
        <div className={phoneBar ? 'col-span-2 min-w-0' : 'flex-1 min-w-0'}>{summary}</div>
        <button
          type="button"
          onClick={cancel}
          className={`text-sm themed-text-secondary hover:themed-text transition-colors ${phoneBar ? `${PHONE_BUTTON} themed-bg-tertiary` : 'px-3 py-1.5 rounded'}`}
        >
          {t('handoff.cancel')}
        </button>
        {confirmButton(phoneBar ? PHONE_BUTTON : 'px-3 py-1.5')}
      </footer>
    </div>
  )
}

/** A phone's bottom bar — a touch device below the desktop width — for both
 *  pickers: one bar, its line then two full-width buttons. With a mouse or
 *  trackpad (a narrow desktop window) the desktop's rows stay. */
export function usePhoneBar(): boolean {
  const isDesktop = useIsDesktop()
  const finePointer = useFinePointer()
  return !isDesktop && !finePointer
}

/** Classes for the two shapes of the button row (see `usePhoneBar`). */
export const PHONE_BAR = 'grid grid-cols-2 gap-3 px-4 pt-2.5 pb-[max(env(safe-area-inset-bottom),0.75rem)]'
export const PHONE_BUTTON = 'justify-center min-h-11 rounded-lg'

/** A mouse or trackpad is attached (Shift-click is possible) — by what the
 *  device reports, not the window's width: a touch laptop has one, an iPad
 *  has one only with a trackpad or mouse connected. Live, so plugging one in
 *  updates the hint. */
const FINE_POINTER = '(any-pointer: fine)'
function useFinePointer(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia?.(FINE_POINTER)
      m?.addEventListener('change', cb)
      return () => m?.removeEventListener('change', cb)
    },
    () => window.matchMedia?.(FINE_POINTER).matches ?? true,
    () => true,
  )
}

/** "Couldn't check this — Try again": a check that failed, and the person's way on. */
export function CheckFailed({ onTryAgain }: { onTryAgain: () => void }) {
  const { t } = useTranslation('files')
  return (
    <span className="text-amber-500">
      {t('handoff.checkFailed')}{' '}
      {/* A finger-sized target around a small word; the line keeps its height. */}
      <button type="button" onClick={onTryAgain} className="underline hover:themed-text px-1 -mx-1 py-2 -my-2">{t('handoff.tryAgain')}</button>
    </span>
  )
}

/** After an answer whose reply was lost: did it land? (Unknown reads as no —
 *  the person may try again, and a second answer to an answered request is a
 *  409, never a second transfer.) */
export async function answeredAfterAll(pid: string): Promise<boolean> {
  try {
    return (await handoffApi.describe(pid)).status === 'answered'
  } catch {
    return false
  }
}

/** Focus on something whose own Enter means something. */
function isControl(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('input, textarea, select, button, a, [role="button"]')
}

/** The i18n key under `handoff.title.` for what a request asks for. */
export function titleKey(select: HandoffReadPolicy['select'], single: boolean): string {
  if (select === 'folder') return single ? 'folder' : 'folders'
  if (select === 'any') return 'any'
  return single ? 'file' : 'files'
}

type T = (key: string, opts?: Record<string, unknown>) => string

/** "Only .csv, image/*" — which kinds the app narrowed the pick to. */
function describeFilter(policy: HandoffReadPolicy, t: T): string {
  const f = policy.filter
  if (!f) return ''
  const types = [...(f.extensions ?? []).map(e => `.${e}`), ...(f.mime ?? [])]
  if (f.nameGlob) types.push(`“${f.nameGlob}”`)
  return types.length ? t('handoff.only', { types: types.join(', ') }) : ''
}

/** "Choose 1 file", "Choose 3 files" (exactly), "Choose up to 5 files",
 *  "Choose one or more files" (no real cap — Papan's ceiling) — with
 *  " · At least 2" when there is a minimum. */
function describeHowMany(policy: HandoffReadPolicy, t: T): string {
  const { min, max } = policy.selection
  if (max === 1) return t(`handoff.howMany.one_${policy.select}`)
  if (min === max) return t(`handoff.howMany.exactly_${policy.select}`, { n: max })
  const parts = [max < HANDOFF_LIMITS.maxSelection
    ? t(`handoff.howMany.upTo_${policy.select}`, { n: max })
    : t(`handoff.howMany.many_${policy.select}`)]
  if (min > 1) parts.push(t('handoff.atLeast', { n: min }))
  return parts.join(' · ')
}

function confirmLabel({ checked, usingOpenFolder, openFolderName, t }: {
  checked: Map<string, PickerSelection>
  usingOpenFolder: boolean
  openFolderName: string
  t: T
}): string {
  if (usingOpenFolder) return t('handoff.importThisFolder', { name: readableName(openFolderName) })
  const items = [...checked.values()]
  const folders = items.filter(i => i.isFolder).length
  const files = items.length - folders
  if (items.length <= 1) return folders === 1 ? t('handoff.importFolder') : t('handoff.open')
  if (folders === 0) return t('handoff.openFiles', { n: files })
  if (files === 0) return t('handoff.importFolders', { n: folders })
  return t('handoff.shareItems', { n: items.length })
}

/** The picker's status line (exported for its own tests). */
export function Summary({ measure, app, policy, count, onlyFiles, onTryAgain, rule }: {
  measure: Measure
  app: string
  policy: HandoffReadPolicy
  count: number
  /** The choice is files alone: counted as selected. */
  onlyFiles: boolean
  onTryAgain: () => void
  /** The rule to say before anything is chosen (and the app's filter). Once
   *  several files are chosen the button carries the count — the line says
   *  only the size. */
  rule: string
}) {
  const { t } = useTranslation('files')
  const { min, max } = policy.selection
  if (count > max) return <span className="text-red-500">{t('handoff.pickAtMost', { n: max })}</span>
  switch (measure.state) {
    case 'idle':
      return <span className="themed-text-secondary" title={rule}>{rule}</span>
    case 'measuring':
      return <span className="themed-text-muted">{t('handoff.measuring')}</span>
    case 'unavailable':
      return <span className="text-red-500">{t('handoff.unavailable')}</span>
    case 'retrying':
      return <span className="text-amber-500">{t('handoff.checkRetrying')}</span>
    case 'checkFailed':
      return <CheckFailed onTryAgain={onTryAgain} />
    case 'ok':
      // Chosen since: still being measured (the effect says so next render).
      if (measure.count !== count) return <span className="themed-text-muted">{t('handoff.measuring')}</span>
      if (count < min) return <span className="themed-text-muted">{t('handoff.pickAtLeast', { n: min })}</span>
      {
        // "selected" only when what is shared IS what was ticked: a link row
        // is no folder but may stand for one (its contents are counted).
        const asTicked = onlyFiles && measure.files === count
        const size = formatStorageBytes(measure.bytes)
        return (
          <span className="themed-text-secondary">
            {asTicked && count > 1
              // The button says how many ("Open 3 files" — "Open" alone for
              // one): the line, the rest.
              ? t('handoff.willShareSize', { size, app })
              : t('handoff.willShare', { files: t(asTicked ? 'handoff.filesSelected' : 'handoff.fileCount', { count: measure.files }), size, app })}
          </span>
        )
      }
    case 'refused': {
      const r = measure.refusal
      const text = r.reason === 'too_large'
        ? t('handoff.tooLarge', { app, limit: formatStorageBytes(r.limit), actual: formatStorageBytes(r.actual) })
        : r.limit === policy.limits.maxFiles
          ? t('handoff.tooMany', { app, limit: r.limit })
          : t('handoff.tooBigToMeasure', { limit: r.limit })
      return <span className="text-red-500">{text}</span>
    }
  }
}
